import { PageHeader } from "@/components/Layout";
import { Check, X, ShieldCheck, Users as UsersIcon, User } from "lucide-react";

const ROLES = [
  { key: "admin", label: "Admin", color: "#0a2540", icon: ShieldCheck, tagline: "Full control" },
  { key: "manager", label: "Manager", color: "#7c3aed", icon: UsersIcon, tagline: "Day-to-day operations" },
  { key: "executive", label: "Executive", color: "#0f766e", icon: User, tagline: "Field/team member" },
];

// Cell shapes:
//   true / false                                  -> Yes / No
//   { admin: ..., manager: ..., executive: ... }  -> per-role override (string or boolean)
const SECTIONS = [
  {
    title: "Clients",
    rows: [
      { label: "Create / edit clients",    cells: { admin: true,  manager: true,  executive: false } },
      { label: "Delete a client",          cells: { admin: true,  manager: false, executive: false } },
      { label: "View clients",             cells: { admin: "All", manager: "All", executive: "Only assigned" } },
    ],
  },
  {
    title: "Projects",
    rows: [
      { label: "Create / edit projects",   cells: { admin: true, manager: true, executive: false } },
      { label: "Delete a project",         cells: { admin: true, manager: false, executive: false } },
      { label: "View projects",            cells: { admin: "All", manager: "All", executive: "Assigned only" } },
      { label: "Update status / Stuck-With", cells: { admin: true, manager: true, executive: "If assigned" } },
    ],
  },
  {
    title: "Tasks",
    rows: [
      { label: "Create tasks",             cells: { admin: true, manager: true, executive: "On accessible projects" } },
      { label: "Edit tasks (status, description, files, links)", cells: { admin: true, manager: true, executive: "If assignee" } },
      { label: "Delete a task",            cells: { admin: true, manager: false, executive: false } },
      { label: "Task priorities (Low/Medium/High) + daily auto-bump for overdue", cells: { admin: "Auto + manual", manager: "Auto + manual", executive: "Auto + manual" } },
      { label: "Export task to PDF (branded)", cells: { admin: true, manager: true, executive: "If accessible" } },
    ],
  },
  {
    title: "Databases (custom)",
    rows: [
      { label: "View databases & entries", cells: { admin: "All", manager: "All", executive: "Only on assigned clients/projects, excl. hidden" } },
      { label: "Create new databases",     cells: { admin: true, manager: true, executive: "On accessible clients/projects" } },
      { label: "Edit schema (columns + name)", cells: { admin: "Any DB", manager: "Any DB", executive: "Only own DBs" } },
      { label: "Add / edit / delete entries", cells: { admin: "Any DB", manager: "Any DB", executive: "Only on own DBs" } },
      { label: "Delete a database",        cells: { admin: "Any DB", manager: "Own DBs only", executive: "Own DBs only" } },
      { label: "Hide DB from Executives toggle", cells: { admin: true, manager: true, executive: false } },
      { label: "Excel export of DB",       cells: { admin: true, manager: true, executive: "Only own DBs" } },
      { label: "Reminder-column type — auto email at 9am + dashboard panel", cells: { admin: true, manager: "If client assigned", executive: "If client assigned & not hidden" } },
    ],
  },
  {
    title: "Project PDF Export (section selector)",
    rows: [
      { label: "Open PDF export dialog (pick sections + tasks + pages)", cells: { admin: true, manager: true, executive: true } },
      { label: "Export project summary",   cells: { admin: true, manager: true, executive: true } },
      { label: "Include description / tasks / pages / comments — toggleable per export", cells: { admin: "All toggles", manager: "All toggles", executive: "All toggles" } },
    ],
  },
  {
    title: "Project Pages / Research",
    rows: [
      { label: "Create / edit pages",      cells: { admin: true, manager: true, executive: "On assigned projects" } },
      { label: "Delete a page",            cells: { admin: true, manager: false, executive: false } },
      { label: "Export page to PDF (branded)", cells: { admin: true, manager: true, executive: true } },
      { label: "Export ALL pages bundle (PDF)", cells: { admin: true, manager: true, executive: true } },
    ],
  },
  {
    title: "Files & Drive Links",
    rows: [
      { label: "Upload / attach files",    cells: { admin: true, manager: true, executive: "On accessible items" } },
      { label: "Attach Google Drive links", cells: { admin: true, manager: true, executive: "On accessible items" } },
    ],
  },
  {
    title: "Comments & @mentions",
    rows: [
      { label: "Post comments + mention teammates", cells: { admin: true, manager: true, executive: "On accessible items" } },
      { label: "Edit / delete own comments",        cells: { admin: true, manager: true, executive: true } },
      { label: "Delete any comment",                cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Leave",
    rows: [
      { label: "Apply (incl. half-day Sick/Casual)", cells: { admin: true, manager: true, executive: true } },
      { label: "Cancel own (Pending or Approved-before-start)", cells: { admin: true, manager: true, executive: true } },
      { label: "Approve / reject team requests",     cells: { admin: true, manager: false, executive: false } },
      { label: "Cancel an approved leave after start date", cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Attendance",
    rows: [
      { label: "Check in / out with geolocation (reverse-geocoded address)", cells: { admin: true, manager: true, executive: true } },
      { label: "View own monthly statement (My Attendance) with worked-hours caption", cells: { admin: true, manager: true, executive: true } },
      { label: "See team monthly stats & daily view", cells: { admin: true, manager: true, executive: false } },
      { label: "Edit an attendance record (check-in / check-out + audit trail)", cells: { admin: true, manager: false, executive: false } },
      { label: "Auto reminder if still checked-in past office_hours_end + 30 / 90 min", cells: { admin: "Receives if applicable", manager: "Receives if applicable", executive: "Receives if applicable" } },
      { label: "Duration-based crediting: ≥ 7 h Present, 3.5–7 h Half-Day, < 3.5 h Short Day (thresholds in Settings)", cells: { admin: true, manager: true, executive: true } },
      { label: "Payroll Register (Excel)",          cells: { admin: true, manager: true, executive: false } },
    ],
  },
  {
    title: "Holidays",
    rows: [
      { label: "View holiday calendar",   cells: { admin: true, manager: true, executive: true } },
      { label: "Add / delete holidays",   cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Staff",
    rows: [
      { label: "View directory",                       cells: { admin: true, manager: true, executive: false } },
      { label: "Invite / role / leave-balance / assign clients", cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Analytics & Activity Log",
    rows: [
      { label: "Analytics dashboard",     cells: { admin: true, manager: true, executive: false } },
      { label: "Activity / Audit Log",    cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Settings",
    rows: [
      { label: "Branding (logo, portal name)",  cells: { admin: true, manager: false, executive: false } },
      { label: "Working Days, Office Hours, Geofence", cells: { admin: true, manager: false, executive: false } },
      { label: "Attendance Crediting (full-day / half-day hour thresholds)", cells: { admin: true, manager: false, executive: false } },
      { label: "Send test email + view Resend status", cells: { admin: true, manager: false, executive: false } },
      { label: "Preview as Manager / Executive (30-min magic URL)", cells: { admin: true, manager: false, executive: false } },
      { label: "External Tools — add / edit / delete", cells: { admin: true, manager: false, executive: false } },
      { label: "External Tools — see in sidebar",      cells: { admin: true, manager: true, executive: true } },
    ],
  },
  {
    title: "Roles & Rights (this page)",
    rows: [
      { label: "View this matrix",  cells: { admin: true, manager: false, executive: false } },
    ],
  },
  {
    title: "Dashboard",
    rows: [
      { label: "Holidays-this-month, My-Pending-Tasks, Reminders-this-month feeds", cells: { admin: true, manager: true, executive: true } },
      { label: "Clients-this-month logos panel", cells: { admin: true, manager: true, executive: "Only assigned" } },
      { label: "Open-checkin reminder banner (past office_hours + 30 min)", cells: { admin: "If still in", manager: "If still in", executive: "If still in" } },
    ],
  },
  {
    title: "Announcements",
    rows: [
      { label: "View announcements on Dashboard (Info / Warning / Critical w/ sound + pulse)", cells: { admin: true, manager: true, executive: true } },
      { label: "Create / post announcements", cells: { admin: true, manager: true, executive: false } },
      { label: "Edit / disable / delete an announcement", cells: { admin: "Any", manager: "Own only", executive: false } },
      { label: "Targeted audiences (All / Managers / Executives / Admins)", cells: { admin: true, manager: true, executive: false } },
    ],
  },
  {
    title: "Notifications & Comments",
    rows: [
      { label: "@mentions in comments + notification bell", cells: { admin: true, manager: true, executive: true } },
      { label: "Email pipeline (Resend live, throttled)", cells: { admin: "Always", manager: "If addressed", executive: "If addressed" } },
    ],
  },
];

function RoleBadge({ role }) {
  const Icon = role.icon;
  return (
    <div className="inline-flex items-center gap-2">
      <div className="h-7 w-7 rounded-sm flex items-center justify-center" style={{ background: role.color }}>
        <Icon size={13} className="text-white" />
      </div>
      <div className="leading-tight">
        <div className="text-sm font-semibold" style={{ fontFamily: "Outfit", color: role.color }}>{role.label}</div>
        <div className="text-[10px] gc-label">{role.tagline}</div>
      </div>
    </div>
  );
}

function Cell({ value, role }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: role.color }} data-testid="cell-yes">
        <Check size={14} strokeWidth={3} />
        <span className="hidden sm:inline">Yes</span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-slate-300" data-testid="cell-no">
        <X size={14} strokeWidth={2.5} />
        <span className="hidden sm:inline">No</span>
      </span>
    );
  }
  // String -> qualified yes
  return (
    <span
      className="inline-block text-xs px-2 py-0.5 rounded-sm font-medium"
      style={{ background: `${role.color}14`, color: role.color }}
      data-testid="cell-qualified"
    >
      {value}
    </span>
  );
}

export default function RolesPermissions() {
  return (
    <div className="min-h-screen bg-white" data-testid="roles-permissions-page">
      <PageHeader title="Roles & Permissions" subtitle="Who can do what in Pulse" />

      <div className="px-4 sm:px-8 py-6 max-w-6xl">
        {/* Role legend */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {ROLES.map((r) => (
            <div key={r.key} className="border border-slate-200 px-4 py-3 rounded-sm" data-testid={`role-card-${r.key}`}>
              <RoleBadge role={r} />
              <div className="mt-3 text-xs text-slate-500 leading-relaxed">
                {r.key === "admin" && "Full access — settings, billing, audit log, employee management, and every operational feature."}
                {r.key === "manager" && "Operates the practice — clients, projects, tasks, databases, attendance review. Cannot delete clients/projects or change settings."}
                {r.key === "executive" && "Field-level access — sees and works on their assigned clients & projects. Files their own attendance, applies for leave, posts comments."}
              </div>
            </div>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-8" data-testid="rbac-matrix">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <div className="border-b border-slate-200 pb-2 mb-3">
                <h2 className="text-sm font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{section.title}</h2>
              </div>
              <div className="border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5 w-1/2">Capability</th>
                      {ROLES.map((r) => (
                        <th key={r.key} className="text-left px-4 py-2.5">{r.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row) => (
                      <tr key={row.label} className="border-b border-slate-100 last:border-b-0" data-testid={`rbac-row-${section.title.toLowerCase().replace(/\s+/g, "-")}-${row.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <td className="px-4 py-2.5 text-slate-700">{row.label}</td>
                        {ROLES.map((r) => (
                          <td key={r.key} className="px-4 py-2.5">
                            <Cell value={row.cells[r.key]} role={r} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 p-4 border border-slate-200 rounded-sm bg-slate-50/50 text-xs text-slate-500 leading-relaxed">
          <strong className="text-[#0a2540]">A note on Executive access:</strong> all data scoping for Executives is enforced server-side via
          <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded-sm font-mono-data">user.assigned_client_ids</code>
          and the project's
          <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded-sm font-mono-data">assignee_ids</code>.
          An Admin can update assignments any time from <strong>Staff → edit user</strong>.
        </div>
      </div>
    </div>
  );
}
